import { useEffect, useState } from "react";
import polandOutline from "@/assets/poland-map.svg";
import "./PolandAnimatedMap.css";

/**
 * Animowana mapa Polski z sekwencyjnym rysowaniem łuków 5G.
 * Sterowanie przez React state (useEffect + setInterval) — bez CSS keyframes.
 *
 * Cykl:
 *  - 11 miast pojawia się kolejno co ~1000 ms (łącznie ~11 s rysowania)
 *  - hold ~3 s ze wszystkimi widocznymi
 *  - reset i pętla
 *  - Całkowity cykl ≈ 14 s
 */
const CENTER = { x: 548, y: 531 };

const CITIES = [
  { name: "GDAŃSK", x: 460, y: 165, sweep: -1 },
  { name: "SZCZECIN", x: 155, y: 280, sweep: -1 },
  { name: "BIAŁYSTOK", x: 810, y: 316, sweep: 1 },
  { name: "POZNAŃ", x: 326, y: 407, sweep: -1 },
  { name: "WARSZAWA", x: 654, y: 428, sweep: 1 },
  { name: "WROCŁAW", x: 334, y: 569, sweep: -1 },
  { name: "KATOWICE", x: 494, y: 673, sweep: 0 },
  { name: "LUBLIN", x: 778, y: 550, sweep: 1 },
  { name: "KRAKÓW", x: 567, y: 698, sweep: 0 },
  { name: "RZESZÓW", x: 732, y: 701, sweep: 1 },
  { name: "ZIELONA GÓRA", x: 212, y: 464, sweep: -1 },
  { name: "RACIBÓRZ", x: 420, y: 720, sweep: -1 },
];

const STEP_MS = 1000;   // czas między pojawieniem kolejnego miasta
const HOLD_MS = 3000;   // pauza po pełnym rysowaniu przed resetem

function arcPath(city) {
  const mx = (CENTER.x + city.x) / 2;
  const my = (CENTER.y + city.y) / 2;
  const dx = city.x - CENTER.x;
  const dy = city.y - CENTER.y;
  const len = Math.hypot(dx, dy) || 1;
  const nx = (-dy / len) * len * 0.18 * city.sweep;
  const ny = (dx / len) * len * 0.18 * city.sweep;
  return `M ${CENTER.x} ${CENTER.y} Q ${mx + nx} ${my + ny} ${city.x} ${city.y}`;
}

export default function PolandAnimatedMap({ className = "" }) {
  // Ile miast/łuków jest obecnie widocznych (0..CITIES.length).
  const [visible, setVisible] = useState(0);

  useEffect(() => {
    let timeoutId;
    let intervalId;

    const runCycle = () => {
      setVisible(0);
      // start rysowania po jednym miejscu co STEP_MS
      intervalId = setInterval(() => {
        setVisible((v) => {
          if (v >= CITIES.length) {
            clearInterval(intervalId);
            // po pokazaniu wszystkich — trzymaj HOLD_MS i restart
            timeoutId = setTimeout(runCycle, HOLD_MS);
            return v;
          }
          return v + 1;
        });
      }, STEP_MS);
    };

    runCycle();

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className={`rm-map-wrap ${className}`} data-testid="poland-animated-map">
      <img src={polandOutline} alt="" aria-hidden="true" className="rm-map-outline" />
      <svg
        viewBox="0 0 1000 947"
        className="rm-map-svg"
        aria-hidden="true"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Łuki */}
        {CITIES.map((c, i) => {
          const isOn = i < visible;
          return (
            <path
              key={`arc-${c.name}`}
              d={arcPath(c)}
              stroke="#7dd3fc"
              strokeWidth="3"
              strokeLinecap="round"
              fill="none"
              style={{
                strokeDasharray: 900,
                strokeDashoffset: isOn ? 0 : 900,
                opacity: isOn ? 1 : 0,
                filter: "drop-shadow(0 0 4px rgba(56, 189, 248, 0.9))",
                transition: "stroke-dashoffset 700ms ease-out, opacity 250ms linear",
              }}
            />
          );
        })}

        {/* Centralna gwiazda — zawsze widoczna */}
        <g className="rm-center">
          <circle
            cx={CENTER.x}
            cy={CENTER.y}
            r="30"
            fill="#38bdf8"
            opacity="0.4"
            style={{ filter: "drop-shadow(0 0 12px rgba(56, 189, 248, 0.8))" }}
          />
          <circle cx={CENTER.x} cy={CENTER.y} r="7" fill="#ffffff" />
          <circle cx={CENTER.x} cy={CENTER.y} r="3.5" fill="#38bdf8" />
        </g>

        {/* Kropki miast + etykiety */}
        {CITIES.map((c, i) => {
          const isOn = i < visible;
          return (
            <g
              key={`c-${c.name}`}
              style={{
                opacity: isOn ? 1 : 0,
                transition: "opacity 400ms ease-out",
                transitionDelay: isOn ? "500ms" : "0ms",
                filter: "drop-shadow(0 0 6px rgba(56, 189, 248, 0.8))",
              }}
            >
              <circle cx={c.x} cy={c.y} r="16" fill="#38bdf8" opacity="0.4" />
              <circle cx={c.x} cy={c.y} r="5" fill="#ffffff" />
              <text
                x={c.x}
                y={c.y - 22}
                textAnchor="middle"
                className="rm-city-label"
              >
                {c.name}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
